import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Bell, BellRing, Plus, Trash2, Pause, Play, CheckCircle2, Pencil, Repeat, Target,
  Loader2, Sparkles, Clock, CalendarDays, Moon,
} from "lucide-react";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { useLanguage } from "@/context/LanguageContext";
import api, { notifyError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const BROWSER_TZ = (() => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Kolkata"; }
  catch { return "Asia/Kolkata"; }
})();

const WEEKDAY_LABELS = {
  en: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
  hi: ["सोमवार", "मंगलवार", "बुधवार", "गुरुवार", "शुक्रवार", "शनिवार", "रविवार"],
};

const EMPTY_FORM = {
  title: "", description: "", type: "none", recurrence: "daily",
  weekday: 0, day_of_month: 1, date: "", time: "09:00",
  goal_id: "", goal_offset_days: "",
};

function formatWhen(r, language, t) {
  if (!r.next_at) return t("rem_no_date");
  const locale = language === "hi" ? "hi-IN" : "en-IN";
  const dt = new Date(r.next_at);
  const timeStr = dt.toLocaleTimeString(locale, { hour: "numeric", minute: "2-digit", timeZone: r.timezone });
  if (r.recurrence === "daily") return `${t("rem_daily")} · ${timeStr}`;
  if (r.recurrence === "weekly") return `${(WEEKDAY_LABELS[language] || WEEKDAY_LABELS.en)[r.weekday ?? 0]} · ${timeStr}`;
  if (r.recurrence === "monthly") return `${t("rem_day")} ${r.day_of_month} · ${timeStr}`;
  const dateStr = dt.toLocaleDateString(locale, { day: "numeric", month: "short", year: "numeric", timeZone: r.timezone });
  return `${dateStr} · ${timeStr}`;
}

export default function Reminders() {
  const { t, language } = useLanguage();
  const [items, setItems] = useState([]);
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [quick, setQuick] = useState("");
  const [parsing, setParsing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notifPerm, setNotifPerm] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "unsupported"
  );

  const load = useCallback(() => {
    setLoading(true);
    api.get("/reminders").then(({ data }) => setItems(data || [])).catch(() => setItems([])).finally(() => setLoading(false));
    api.get("/goals").then(({ data }) => setGoals(data || [])).catch(() => setGoals([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const onFired = () => load();
    window.addEventListener("evermin:reminder-fired", onFired);
    return () => window.removeEventListener("evermin:reminder-fired", onFired);
  }, [load]);

  const enableNotifications = async () => {
    if (typeof Notification === "undefined") return;
    try {
      const p = await Notification.requestPermission();
      setNotifPerm(p);
      if (p === "granted") toast.success(t("rem_notif_on"));
      else toast.message(t("rem_notif_blocked"));
    } catch { /* ignore */ }
  };

  const openCreate = () => { setEditingId(null); setForm(EMPTY_FORM); setShowForm(true); };

  const openEdit = (r) => {
    setEditingId(r.reminder_id);
    setForm({
      title: r.title || "", description: r.description || "",
      type: r.recurrence === "none" ? "none" : "recurring",
      recurrence: r.recurrence === "none" ? "daily" : r.recurrence,
      weekday: r.weekday ?? 0, day_of_month: r.day_of_month ?? 1,
      date: r.date || "", time: r.time || "09:00",
      goal_id: r.goal_id || "", goal_offset_days: r.goal_offset_days != null ? String(r.goal_offset_days) : "",
    });
    setShowForm(true);
  };

  const quickAdd = async () => {
    const text = quick.trim();
    if (!text) return;
    setParsing(true);
    try {
      const { data } = await api.post("/reminders/parse", { text, timezone: BROWSER_TZ });
      const f = { ...EMPTY_FORM };
      f.title = data.title || text.slice(0, 80);
      f.time = data.time || "09:00";
      if (data.recurrence && data.recurrence !== "none") {
        f.type = "recurring"; f.recurrence = data.recurrence;
        if (data.weekday != null) f.weekday = data.weekday;
        if (data.day_of_month != null) f.day_of_month = data.day_of_month;
      } else {
        f.type = "none";
        if (data.date) f.date = data.date;
      }
      if (data.goal_relative && data.offset_days != null) {
        f.goal_offset_days = String(data.offset_days);
        f.type = "none";
      }
      setForm(f);
      setEditingId(null);
      setShowForm(true);
      setQuick("");
      if (!data.parsed) toast.message(t("rem_parse_partial"));
    } catch (e) {
      notifyError(e);
    } finally {
      setParsing(false);
    }
  };

  const buildPayload = () => {
    const goalLinked = !!form.goal_offset_days && !!form.goal_id;
    const payload = {
      title: form.title.trim(),
      description: form.description.trim(),
      timezone: BROWSER_TZ,
      time: form.time,
      recurrence: form.type === "recurring" ? form.recurrence : "none",
      goal_id: form.goal_id || null,
    };
    if (payload.recurrence === "weekly") payload.weekday = Number(form.weekday);
    if (payload.recurrence === "monthly") payload.day_of_month = Number(form.day_of_month);
    if (payload.recurrence === "none" && !goalLinked && form.date) payload.date = form.date;
    if (goalLinked) payload.goal_offset_days = Number(form.goal_offset_days);
    return { payload, goalLinked };
  };

  const validate = ({ payload, goalLinked }) => {
    if (payload.title.length < 2) return t("rem_err_title");
    if (!payload.time) return t("rem_err_time");
    if (payload.recurrence === "none" && !goalLinked && !payload.date) return t("rem_err_date");
    if (goalLinked && !payload.goal_id) return t("rem_err_goal");
    return null;
  };

  const save = async () => {
    const built = buildPayload();
    const err = validate(built);
    if (err) { toast.error(err); return; }
    setSaving(true);
    try {
      if (editingId) {
        const { data } = await api.put(`/reminders/${editingId}`, built.payload);
        setItems((prev) => prev.map((x) => (x.reminder_id === editingId ? data : x)));
        toast.success(t("rem_updated"));
      } else {
        const { data } = await api.post("/reminders", built.payload);
        setItems((prev) => [...prev, data]);
        toast.success(t("rem_created"));
      }
      setShowForm(false);
    } catch (e) {
      if (e?.response?.data?.detail === "GOAL_DATE_UNPARSEABLE") toast.error(t("rem_err_goaldate"));
      else notifyError(e);
    } finally {
      setSaving(false);
    }
  };

  const act = async (r, action) => {
    try {
      const { data } = await api.post(`/reminders/${r.reminder_id}/${action}`);
      setItems((prev) => prev.map((x) => (x.reminder_id === r.reminder_id ? data : x)));
    } catch (e) { notifyError(e); }
  };

  const snooze = async (r, mode) => {
    try {
      const { data } = await api.post(`/reminders/${r.reminder_id}/snooze`, { mode });
      setItems((prev) => prev.map((x) => (x.reminder_id === r.reminder_id ? data : x)));
      toast.success(t("rem_snoozed"));
    } catch (e) { notifyError(e); }
  };

  const remove = async (r) => {
    try {
      await api.delete(`/reminders/${r.reminder_id}`);
      setItems((prev) => prev.filter((x) => x.reminder_id !== r.reminder_id));
      toast.success(t("rem_deleted"));
    } catch (e) { notifyError(e); }
  };

  const goalName = (id) => goals.find((g) => g.goal_id === id)?.title;

  const groups = useMemo(() => {
    const due = items.filter((r) => r.due);
    const upcoming = items.filter((r) => r.status === "active" && r.recurrence === "none" && !r.due);
    const recurring = items.filter((r) => r.status === "active" && r.recurrence !== "none");
    const paused = items.filter((r) => r.status === "paused");
    const completed = items.filter((r) => r.status === "completed");
    return { due, upcoming, recurring, paused, completed };
  }, [items]);

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Header />
      <main className="mx-auto w-full max-w-4xl flex-1 px-4 py-8 sm:px-6 sm:py-10 lg:px-8">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-950 sm:text-4xl" data-testid="reminders-title">{t("rem_title")}</h1>
            <p className="mt-1.5 text-slate-500">{t("rem_subtitle")}</p>
          </div>
          <Button onClick={openCreate} data-testid="reminder-new-btn" className="gap-1.5 rounded-full min-h-[44px]">
            <Plus className="h-4 w-4" /> {t("rem_new")}
          </Button>
        </div>

        {/* Natural-language quick add */}
        <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm sm:p-4" data-testid="reminder-quickadd">
          <div className="flex flex-col gap-2 sm:flex-row">
            <Input
              value={quick}
              onChange={(e) => setQuick(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && quickAdd()}
              placeholder={t("rem_quick_ph")}
              data-testid="reminder-quick-input"
              className="min-h-[44px] flex-1"
            />
            <Button onClick={quickAdd} disabled={parsing || !quick.trim()} data-testid="reminder-quick-btn" className="min-h-[44px] gap-1.5 rounded-xl">
              {parsing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} {t("rem_quick_add")}
            </Button>
          </div>
          <p className="mt-2 text-xs text-slate-400">{t("rem_quick_hint")}</p>
        </div>

        {/* Notification permission nudge */}
        {notifPerm !== "granted" && notifPerm !== "unsupported" && (
          <button onClick={enableNotifications} data-testid="reminder-enable-notif"
            className="mt-4 flex w-full items-center gap-2.5 rounded-xl border border-primary/20 bg-primary/5 px-4 py-3 text-left text-sm text-slate-700 transition-colors hover:bg-primary/10">
            <BellRing className="h-4 w-4 flex-shrink-0 text-primary" />
            <span>{notifPerm === "denied" ? t("rem_notif_blocked") : t("rem_enable_notif")}</span>
          </button>
        )}

        {loading ? (
          <div className="mt-10 flex justify-center" data-testid="reminders-loading"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div>
        ) : items.length === 0 ? (
          <div className="mt-10 rounded-2xl border border-dashed border-slate-200 bg-white p-10 text-center" data-testid="reminders-empty">
            <Bell className="mx-auto h-8 w-8 text-slate-300" />
            <p className="mt-3 font-display text-lg font-semibold text-slate-900">{t("rem_empty_title")}</p>
            <p className="mt-1 text-sm text-slate-500">{t("rem_empty_desc")}</p>
          </div>
        ) : (
          <div className="mt-8 space-y-8">
            <Section title={t("rem_due")} tone="due" items={groups.due} {...{ t, language, act, snooze, remove, openEdit, goalName }} />
            <Section title={t("rem_upcoming")} icon={Clock} items={groups.upcoming} {...{ t, language, act, snooze, remove, openEdit, goalName }} />
            <Section title={t("rem_recurring")} icon={Repeat} items={groups.recurring} {...{ t, language, act, snooze, remove, openEdit, goalName }} />
            <Section title={t("rem_paused")} icon={Pause} items={groups.paused} {...{ t, language, act, snooze, remove, openEdit, goalName }} />
            <Section title={t("rem_completed")} icon={CheckCircle2} items={groups.completed} {...{ t, language, act, snooze, remove, openEdit, goalName }} />
          </div>
        )}
      </main>
      <Footer />

      <ReminderForm
        open={showForm} onOpenChange={setShowForm} form={form} setForm={setForm}
        goals={goals} onSave={save} saving={saving} editing={!!editingId} t={t} language={language}
      />
    </div>
  );
}

function Section({ title, icon: Icon, tone, items, t, language, act, snooze, remove, openEdit, goalName }) {
  if (!items.length) return null;
  const testid = `reminder-section-${title.toLowerCase().replace(/\s+/g, "-")}`;
  return (
    <section data-testid={testid}>
      <h2 className={`mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-wide ${tone === "due" ? "text-rose-600" : "text-slate-500"}`}>
        {tone === "due" ? <BellRing className="h-4 w-4" /> : Icon ? <Icon className="h-4 w-4" /> : null}
        {title} <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500">{items.length}</span>
      </h2>
      <div className="space-y-3">
        {items.map((r) => (
          <ReminderCard key={r.reminder_id} r={r} due={tone === "due"} {...{ t, language, act, snooze, remove, openEdit, goalName }} />
        ))}
      </div>
    </section>
  );
}

function ReminderCard({ r, due, t, language, act, snooze, remove, openEdit, goalName }) {
  const gname = r.goal_id ? goalName(r.goal_id) : null;
  return (
    <div data-testid={`reminder-card-${r.reminder_id}`}
      className={`rounded-2xl border bg-white p-4 shadow-sm ${due ? "border-rose-200 ring-1 ring-rose-100" : "border-slate-200"}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-display text-base font-semibold text-slate-900 [overflow-wrap:anywhere]" data-testid="reminder-card-title">{r.title}</p>
            {r.recurrence !== "none" && <Badge variant="outline" className="gap-1 border-primary/20 bg-primary/5 text-primary"><Repeat className="h-3 w-3" /> {t("rem_recurring")}</Badge>}
            {r.goal_offset_days != null && <Badge variant="outline" className="border-violet-200 bg-violet-50 text-violet-700">{r.goal_offset_days} {t("rem_days_before")}</Badge>}
          </div>
          {r.description && <p className="mt-1 text-sm text-slate-500 [overflow-wrap:anywhere]">{r.description}</p>}
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
            <span className="inline-flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" /> {formatWhen(r, language, t)}</span>
            {gname && <span className="inline-flex items-center gap-1 text-violet-600"><Target className="h-3.5 w-3.5" /> {gname}</span>}
            {r.last_fired_at && <span className="text-emerald-600">{t("rem_last_notified")}</span>}
          </div>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {due && (
          <>
            <IconBtn testid={`reminder-snooze-1h-${r.reminder_id}`} onClick={() => snooze(r, "1h")} label={t("rem_snooze_1h")} accent><Clock className="h-4 w-4" /></IconBtn>
            <IconBtn testid={`reminder-snooze-evening-${r.reminder_id}`} onClick={() => snooze(r, "evening")} label={t("rem_snooze_evening")} accent><Moon className="h-4 w-4" /></IconBtn>
          </>
        )}
        {r.status !== "completed" && (
          <IconBtn testid={`reminder-complete-${r.reminder_id}`} onClick={() => act(r, "complete")} label={t("rem_complete")}><CheckCircle2 className="h-4 w-4" /></IconBtn>
        )}
        {r.status === "active" && (
          <IconBtn testid={`reminder-pause-${r.reminder_id}`} onClick={() => act(r, "pause")} label={t("rem_pause")}><Pause className="h-4 w-4" /></IconBtn>
        )}
        {(r.status === "paused" || r.status === "completed") && (
          <IconBtn testid={`reminder-resume-${r.reminder_id}`} onClick={() => act(r, "resume")} label={t("rem_resume")}><Play className="h-4 w-4" /></IconBtn>
        )}
        <IconBtn testid={`reminder-edit-${r.reminder_id}`} onClick={() => openEdit(r)} label={t("rem_edit")}><Pencil className="h-4 w-4" /></IconBtn>
        <IconBtn testid={`reminder-delete-${r.reminder_id}`} onClick={() => remove(r)} label={t("rem_delete")} danger><Trash2 className="h-4 w-4" /></IconBtn>
      </div>
    </div>
  );
}

function IconBtn({ children, onClick, label, testid, danger, accent }) {
  const tone = danger
    ? "border-rose-200 text-rose-600 hover:bg-rose-50"
    : accent
      ? "border-primary/30 bg-primary/5 text-primary hover:bg-primary/10"
      : "border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-900";
  return (
    <button onClick={onClick} data-testid={testid}
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors min-h-[36px] ${tone}`}>
      {children} <span className="hidden sm:inline">{label}</span>
    </button>
  );
}

function ReminderForm({ open, onOpenChange, form, setForm, goals, onSave, saving, editing, t, language }) {
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const wdLabels = WEEKDAY_LABELS[language] || WEEKDAY_LABELS.en;
  const goalLinked = !!form.goal_offset_days;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="reminder-form">
        <DialogHeader>
          <DialogTitle>{editing ? t("rem_edit_title") : t("rem_form_title")}</DialogTitle>
          <DialogDescription>{t("rem_form_desc")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <div>
            <Label>{t("rem_field_title")}</Label>
            <Input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="reminder-input-title" className="mt-1.5 min-h-[44px]" placeholder={t("rem_field_title_ph")} />
          </div>
          <div>
            <Label>{t("rem_field_desc")}</Label>
            <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="reminder-input-desc" className="mt-1.5" rows={2} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{t("rem_type")}</Label>
              <Select value={form.type} onValueChange={(v) => set("type", v)}>
                <SelectTrigger className="mt-1.5 min-h-[44px]" data-testid="reminder-select-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none" data-testid="reminder-type-onetime">{t("rem_onetime")}</SelectItem>
                  <SelectItem value="recurring" data-testid="reminder-type-recurring">{t("rem_recurring_t")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t("rem_time")}</Label>
              <Input type="time" value={form.time} onChange={(e) => set("time", e.target.value)} data-testid="reminder-input-time" className="mt-1.5 min-h-[44px]" />
            </div>
          </div>

          {form.type === "recurring" ? (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t("rem_recurrence")}</Label>
                <Select value={form.recurrence} onValueChange={(v) => set("recurrence", v)}>
                  <SelectTrigger className="mt-1.5 min-h-[44px]" data-testid="reminder-select-recurrence"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">{t("rem_daily")}</SelectItem>
                    <SelectItem value="weekly">{t("rem_weekly")}</SelectItem>
                    <SelectItem value="monthly">{t("rem_monthly")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.recurrence === "weekly" && (
                <div>
                  <Label>{t("rem_weekday")}</Label>
                  <Select value={String(form.weekday)} onValueChange={(v) => set("weekday", Number(v))}>
                    <SelectTrigger className="mt-1.5 min-h-[44px]" data-testid="reminder-select-weekday"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {wdLabels.map((w, i) => <SelectItem key={i} value={String(i)}>{w}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {form.recurrence === "monthly" && (
                <div>
                  <Label>{t("rem_dom")}</Label>
                  <Input type="number" min="1" max="31" value={form.day_of_month} onChange={(e) => set("day_of_month", e.target.value)} data-testid="reminder-input-dom" className="mt-1.5 min-h-[44px]" />
                </div>
              )}
            </div>
          ) : !goalLinked ? (
            <div>
              <Label>{t("rem_date")}</Label>
              <Input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} data-testid="reminder-input-date" className="mt-1.5 min-h-[44px]" />
            </div>
          ) : null}

          {/* Goal link */}
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
            <Label className="flex items-center gap-1.5"><Target className="h-3.5 w-3.5 text-violet-600" /> {t("rem_link_goal")}</Label>
            <Select value={form.goal_id || "none"} onValueChange={(v) => set("goal_id", v === "none" ? "" : v)}>
              <SelectTrigger className="mt-1.5 min-h-[44px]" data-testid="reminder-select-goal"><SelectValue placeholder={t("rem_none")} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{t("rem_none")}</SelectItem>
                {goals.map((g) => <SelectItem key={g.goal_id} value={g.goal_id}>{g.title}</SelectItem>)}
              </SelectContent>
            </Select>
            {form.goal_id && (
              <div className="mt-2">
                <Label className="text-xs text-slate-500">{t("rem_offset_label")}</Label>
                <Input type="number" min="0" max="365" value={form.goal_offset_days} onChange={(e) => set("goal_offset_days", e.target.value)}
                  data-testid="reminder-input-offset" className="mt-1 min-h-[40px]" placeholder={t("rem_offset_ph")} />
                <p className="mt-1 text-xs text-slate-400">{t("rem_offset_hint")}</p>
              </div>
            )}
          </div>

          <p className="text-xs text-slate-400">{t("rem_tz_note")}: <span className="font-medium text-slate-500">{BROWSER_TZ}</span></p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="reminder-cancel-btn" className="rounded-full">{t("rem_cancel")}</Button>
          <Button onClick={onSave} disabled={saving} data-testid="reminder-save-btn" className="gap-1.5 rounded-full">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bell className="h-4 w-4" />} {t("rem_save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
